import { useEffect, useState } from "react";
import { ReceiptText, Check, Undo2, Loader2, Banknote, AlertCircle, BellRing } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const nok = (v) => `${(v || 0).toLocaleString("nb-NO", { minimumFractionDigits: 2 })} kr`;
const fmtDate = (iso) => (iso || "").slice(0, 10).split("-").reverse().join(".");

export default function AdminInvoicesPage() {
  const { organizer } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [reminding, setReminding] = useState(null);

  const sendReminder = async (inv) => {
    setReminding(inv.id);
    try {
      const res = await api.post(`/admin/invoices/${inv.id}/send-reminder`);
      toast.success(`Purring sendt til ${res.data.sent_to} (purring nr. ${res.data.reminder_count})`);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setReminding(null);
    }
  };

  const load = () => {
    api
      .get("/admin/invoices")
      .then((res) => setData(res.data))
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const act = async (inv, action) => {
    setWorking(inv.id);
    try {
      await api.post(`/admin/invoices/${inv.id}/${action}`);
      toast.success(action === "mark-paid" ? `Faktura ${inv.invoice_number} markert som betalt` : `Faktura ${inv.invoice_number} satt tilbake til utestående`);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking(null);
    }
  };

  if (organizer && !organizer.is_admin) {
    return (
      <DashboardLayout>
        <p className="text-muted-foreground" data-testid="admin-invoices-denied">Denne siden krever plattform-administrator.</p>
      </DashboardLayout>
    );
  }

  const isOverdue = (inv) => inv.status !== "paid" && inv.due_date && inv.due_date < new Date().toISOString();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2.5" data-testid="admin-invoices-title">
            <ReceiptText className="w-6 h-6" />
            Fakturaer (admin)
          </h1>
          <p className="text-muted-foreground mt-2">Oversikt over alle fakturaer – marker som betalt når pengene er mottatt.</p>
        </div>

        {loading ? (
          <Skeleton className="h-40 rounded-xl" />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="border-border" data-testid="admin-invoices-outstanding-card">
                <CardContent className="p-5">
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4" />
                    Utestående
                  </p>
                  <p className="text-2xl font-heading font-semibold tabular-nums mt-1" data-testid="admin-invoices-outstanding-total">
                    {nok(data?.outstanding_total)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{data?.outstanding_count || 0} fakturaer</p>
                </CardContent>
              </Card>
              <Card className="border-border" data-testid="admin-invoices-paid-card">
                <CardContent className="p-5">
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Banknote className="w-4 h-4" />
                    Betalt
                  </p>
                  <p className="text-2xl font-heading font-semibold tabular-nums mt-1" data-testid="admin-invoices-paid-total">
                    {nok(data?.paid_total)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{data?.paid_count || 0} fakturaer</p>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border">
              <CardContent className="p-0">
                {(data?.invoices || []).length === 0 ? (
                  <div className="p-10 text-center" data-testid="admin-invoices-empty">
                    <ReceiptText className="w-9 h-9 mx-auto text-muted-foreground mb-3" />
                    <h3 className="font-heading font-semibold">Ingen fakturaer ennå</h3>
                    <p className="text-sm text-muted-foreground mt-1">Fakturaer opprettes automatisk 24 timer etter avsluttede arrangementer.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-invoices-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fakturanr.</TableHead>
                          <TableHead>Arrangør</TableHead>
                          <TableHead>Arrangement</TableHead>
                          <TableHead className="text-right">Beløp</TableHead>
                          <TableHead>Forfall</TableHead>
                          <TableHead>Purring</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Handling</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.invoices.map((inv) => (
                          <TableRow key={inv.id} data-testid={`admin-invoice-row-${inv.invoice_number}`}>
                            <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                            <TableCell className="text-sm">
                              {inv.org_name}
                              <span className="block text-xs text-muted-foreground">{inv.org_email}</span>
                            </TableCell>
                            <TableCell className="text-sm">{inv.event_title}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums font-medium">{nok(inv.total)}</TableCell>
                            <TableCell className={`text-sm ${isOverdue(inv) ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                              {fmtDate(inv.due_date)}
                              {isOverdue(inv) && <span className="block text-xs">Forfalt</span>}
                            </TableCell>
                            <TableCell className="text-sm" data-testid={`admin-invoice-reminders-${inv.invoice_number}`}>
                              {inv.reminder_count ? (
                                <span className="flex items-center gap-1.5 text-muted-foreground">
                                  <BellRing className="w-3.5 h-3.5 text-[hsl(var(--danger))]" />
                                  <span>
                                    {inv.reminder_count}×
                                    <span className="block text-xs">sist {fmtDate(inv.reminder_last_sent_at)}</span>
                                  </span>
                                </span>
                              ) : isOverdue(inv) ? (
                                <span className="text-xs text-muted-foreground">Venter (auto)</span>
                              ) : (
                                <span className="text-muted-foreground">–</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {inv.status === "paid" ? (
                                <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid={`admin-invoice-status-paid-${inv.invoice_number}`}>
                                  Betalt
                                </Badge>
                              ) : (
                                <Badge variant="secondary" data-testid={`admin-invoice-status-sent-${inv.invoice_number}`}>Utestående</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {inv.status !== "paid" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-[hsl(var(--danger))]"
                                    onClick={() => sendReminder(inv)}
                                    disabled={working === inv.id || reminding === inv.id}
                                    title="Send purring på e-post nå"
                                    data-testid={`admin-invoice-remind-${inv.invoice_number}`}
                                  >
                                    {reminding === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4 mr-1.5" />}
                                    Send purring
                                  </Button>
                                )}
                                {inv.status === "paid" ? (
                                  <Button variant="ghost" size="sm" onClick={() => act(inv, "mark-unpaid")} disabled={working === inv.id} data-testid={`admin-invoice-unpaid-${inv.invoice_number}`}>
                                    {working === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1.5" />}
                                    Angre
                                  </Button>
                                ) : (
                                  <Button size="sm" onClick={() => act(inv, "mark-paid")} disabled={working === inv.id} data-testid={`admin-invoice-paid-${inv.invoice_number}`}>
                                    {working === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                                    Marker betalt
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
